import { useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../contexts/user";
import { createAnswer, getAnswersByBlockAndUser, updateAnswer } from "../axios/axiosFunctions";
import { Alert, Box, Button, Grid, TextField, Typography } from "@mui/material"
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import SaveIcon from '@mui/icons-material/Save';
import SaveReminder from './SaveReminder';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Title);

const BlockReport = ({info,blockId,color,questionLength,onChartDataReady,onBlockNoteChange}) => {

    const [currentBlockText, setCurrentBlockText] = useState("");
    const [answerText, setAnswerText] = useState("");
    const [defaultAnswer, setDefaultAnswer] = useState();
    const [questionsAnswers, setQuestionsAnswers] = useState([]);
    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("error");
    const {currentUser} = useContext(UserContext);

    useEffect(() => {
        const getAnswers = async()=>{
            const res = await getAnswersByBlockAndUser(blockId,currentUser._id);
            if(res){ //TODO MANAGE ERROR
                if(res?.answers.length > questionLength){
                    const DEFAULT_ANSWERS = ["notConfident", "somewhat", "confident", "very"];
                    const defValueReportQuestion = res.answers.pop();
                    setDefaultAnswer(defValueReportQuestion);
                    const noteVal = DEFAULT_ANSWERS.includes(defValueReportQuestion.answer) ? "" : (defValueReportQuestion.answer || "");
                    setAnswerText(noteVal);
                    onBlockNoteChange?.(blockId, noteVal);
                }
                const data = res.answers.map((blockInfo)=>({
                    resp: formatResp(blockInfo.answer),
                    question: stripTags(blockInfo.question.text)
                }));
                setQuestionsAnswers(data);
                onChartDataReady?.(blockId, data);
            }
        }
        getAnswers();
    }, [blockId,currentUser,questionLength]);

    useEffect(() => {
        const infoBlock = info.find((b)=>b.block === blockId);
        setCurrentBlockText(infoBlock?.text || "");
    }, [blockId,info]);

    const stripTags = (text = "") =>
        text
          .replace(/<\/?u>/gi, "")
          .replace(/<\/?strong>/gi, "");

    function wrapText(text, maxLineLength) {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';
        words.forEach(word => {
            if (currentLine.length + word.length + 1 <= maxLineLength) {
                currentLine += (currentLine === '' ? '' : ' ') + word;
            } else {
                lines.push(currentLine);
                currentLine = word;
            }
        });
        lines.push(currentLine);
        return lines.filter(line => line.length > 0);
    }

    const formatResp = (resp)=>{
        const obj = {
            notConfident: 1,
            somewhat: 2,
            confident: 3,
            very: 4
        }
        return obj[resp] || 0;
    }

    const scoreLabels = {
        0: [""],
        1: ["Not", "Confident"],
        2: ["Somewhat", "Confident"],
        3: ["Confident"],
        4: ["Very", "Confident"],
    };

    const chartData = useMemo(() => ({
        labels: questionsAnswers.map((d) => d.question),
        datasets: [
            {
                label: "Score",
                data: questionsAnswers.map((d) => d.resp),
                backgroundColor: color,
                borderRadius: 3,
                barThickness: 20,
            },
        ],
    }), [questionsAnswers, color]);

    const chartOptions = useMemo(() => ({
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
            datalabels: { display: false },
        },
        scales: {
            x: {
                type: "linear",
                min: 0,
                max: 4,
                position: "top",
                ticks: {
                    stepSize: 1,
                    font: { size: 12 },
                    color: "#555",
                    maxRotation: 0,
                    minRotation: 0,
                    callback: (value) => scoreLabels[value] || "",
                },
                grid: {
                    color: "rgba(0,0,0,0.08)",
                },
            },
            y: {
                ticks: {
                    autoSkip: false,
                    color: "#333",
                    callback: function (value) {
                        const text = this.getLabelForValue(value);
                        return wrapText(text, 50);
                    },
                    font: { size: 12 },
                    padding: 8,
                },
                grid: {
                    display: false,
                },
            },
        },
    }), []);

    const handleChange = (e)=>{
        const {value} = e.target;
        setAnswerText(value);
    }

    const submitAnswer = async()=>{
        let res;

        if(defaultAnswer){
            const data = {
                answer: answerText
            }
            res = await updateAnswer(defaultAnswer._id,data);
        }else{
            const data = {
                user: currentUser._id,
                block: blockId,
                question: "68e57ea9942cdf0b99834887",
                answer: answerText
            }
            res = await createAnswer(data);
        }

        if(!res){
            setError("Error while saving please try later");
            setShowError(true);
            setTimeout(() => {
              setShowError(false);
            }, 4000);
            return;
        }
        onBlockNoteChange?.(blockId, answerText);
        setDefaultAnswer(res.answer);

        setError("Saved correctly");
        setAlertSeverity("success");
        setShowError(true);
        setTimeout(() => {
            setShowError(false);
        }, 2000);

    }

    return (
        <Box display="flex" flexDirection="column" sx={{padding:{xs:"0px 16px",sm:"0px 24px",md:"0px 48px"}}}>
            <Grid container spacing={2} sx={{margin:"32px 0px 40px 0px"}}>
                <Grid size={12}>
                    <Typography variant="body1" fontWeight={400} sx={{marginTop:"4px",lineHeight:"1.7",fontSize:"1.05rem",whiteSpace:"pre-wrap"}}>{currentBlockText}</Typography>
                </Grid>
                <Grid size={12}>
                    <div id={`chart-container-${blockId}`} style={{height: questionsAnswers.length * 75 + 60, maxWidth: "100%"}}>
                        {questionsAnswers.length > 0 && (
                            <Bar data={chartData} options={chartOptions} />
                        )}
                    </div>
                </Grid>
                <Grid size={12}>
                    <Typography variant="body1" fontWeight={600} color={color} sx={{fontSize:"1.05rem",marginBottom:"8px"}}>What stands out as a priority focus area in this section? Where might you want to focus?</Typography>
                    <TextField value={answerText} onChange={handleChange} fullWidth multiline rows={5}/>
                </Grid>
                {
                    showError &&
                    <Grid size={12}>
                        <Alert sx={{marginTop:"20px"}} severity={alertSeverity}>
                            {error}
                        </Alert>
                    </Grid>
                }
                <Grid sx={{display:"flex",flexDirection:"column",alignItems:"flex-end"}} size={12}>
                    <Button onClick={submitAnswer} variant="contained" sx={{marginTop:"20px"}} startIcon={<SaveIcon/>}>Save</Button>
                    <SaveReminder/>
                </Grid>
            </Grid>
        </Box>
    )
}

export default BlockReport
